import React, { FC, useState, useEffect, useMemo, useCallback } from 'react';
import Skeleton from 'react-loading-skeleton';
import { Account } from 'shared/types/account';
import { Store, StoreOpeningTime } from 'shared/types/cart';
import { Order } from 'shared/types/cart/Order';
import Typography from 'components/commercetools-ui/atoms/typography';
import { useFormat } from 'helpers/hooks/useFormat';
import { useAccount, useCart } from 'frontastic';
import useOrderInfoData from '../hooks/useOrderInfoData';

type ThankYouOrderInfoProps = {
  firstName?: Account['firstName'];
  lastName?: Account['lastName'];
  email?: Account['email'];
  order?: Order;
};

const ThankYouOrderInfo: FC<ThankYouOrderInfoProps> = ({ firstName, lastName, email, order }) => {
  const { formatMessage } = useFormat({ name: 'thank-you' });
  const { loggedIn } = useAccount();
  const { getStores, getStoreOpeningTime } = useCart();
  const loading = !order;

  const {
    orderNumber,
    orderDate,
    deliveryMethod,
    deliveryMethodKey,
    deliveryMode,
    deliveryDate,
    shippingAddress,
    paymentInfo,
    billingAddress,
    billingName,
    deliveryInstruction,
  } = useOrderInfoData(order);

  const [selectedStore, setSelectedStore] = useState<Store | null>(null);
  const [storeOpeningTime, setStoreOpeningTime] = useState<StoreOpeningTime | null>(null);
  const [storeLoading, setStoreLoading] = useState(true);
  const [openingTimeLoading, setOpeningTimeLoading] = useState(true);

  const labelClass = 'w-fit text-14 leading-loose text-secondary-black md:text-16 font-medium';
  const valueClass = 'text-14 font-normal leading-loose text-primary-black md:text-16';
  const subValueClass = 'mt-4 w-fit text-14 leading-loose text-primary-black md:ml-156 md:text-16 font-normal';

  const isStoreCollection = order?.custom?.deliveryOption === 'StoreCollection';

  const orderInfo = useMemo(
    () => [
      { label: formatMessage({ id: 'order.number', defaultMessage: 'Order number' }), value: orderNumber || '' },
      { label: formatMessage({ id: 'order.date', defaultMessage: 'Order date' }), value: orderDate },
      {
        label: formatMessage({ id: 'delivery.method', defaultMessage: 'Delivery method' }),
        value: deliveryMode || '',
      },
      {
        label: formatMessage({ id: 'delivery.by', defaultMessage: 'Delivery by' }),
        value: isStoreCollection
          ? ''
          : deliveryMethod
          ? deliveryMethodKey == 'choosedelivery'
            ? deliveryDate
            : deliveryMethod
          : '',
      },
      {
        label: formatMessage({ id: 'delivery.to', defaultMessage: 'Delivery to' }),
        value: `${firstName ?? ''} ${lastName ?? ''}`,
        subValue: shippingAddress || '',
      },
      {
        label: formatMessage({ id: 'delivery.instruction', defaultMessage: 'Delivery Instruction' }),
        value: isStoreCollection ? '' : deliveryInstruction || '',
      },
      {
        label: formatMessage({ id: 'billing.to', defaultMessage: 'Billing to' }),
        value: billingName || undefined,
        subValue: billingAddress || undefined,
      },
      {
        label: formatMessage({ id: 'email.address', defaultMessage: 'Email address' }),
        value: loggedIn ? email ?? '' : isStoreCollection ? '' : email ?? '',
      },
      { label: formatMessage({ id: 'payment.by', defaultMessage: 'Payment by' }), value: paymentInfo || '' },
    ],
    [
      orderNumber,
      orderDate,
      deliveryMode,
      deliveryMethod,
      firstName,
      lastName,
      shippingAddress,
      deliveryInstruction,
      billingName,
      billingAddress,
      loggedIn,
      email,
      paymentInfo,
      formatMessage,
      isStoreCollection,
    ],
  );

  const selectedStoreAddress1 = useMemo(
    () =>
      selectedStore?.address2 ? `${selectedStore.address1}, ${selectedStore.address2}` : selectedStore?.address1 || '',
    [selectedStore],
  );

  const selectedStoreAddress2 = useMemo(
    () => (selectedStore ? `${selectedStore.city}, ${selectedStore.postalCode}, ${selectedStore.countryCode}` : ''),
    [selectedStore],
  );

  const selectedStoreEmail = useMemo(
    () =>
      selectedStore?.email ? `${formatMessage({ id: 'email', defaultMessage: 'Email' })}: ${selectedStore.email}` : '',
    [selectedStore, formatMessage],
  );

  const selectedStorePhone = useMemo(
    () =>
      selectedStore?.phone ? `${formatMessage({ id: 'phone', defaultMessage: 'Phone' })}: ${selectedStore.phone}` : '',
    [selectedStore, formatMessage],
  );

  const fetchStoreOpeningTime = useCallback(
    async (googlePlaceID: string) => {
      setOpeningTimeLoading(true);
      try {
        const storeOpeningTime = await (getStoreOpeningTime?.(googlePlaceID ?? '') ?? Promise.resolve(null));
        setStoreOpeningTime(storeOpeningTime);
      } catch (error) {
        console.error('Failed to fetch the opening time:', error);
      } finally {
        setOpeningTimeLoading(false);
      }
    },
    [getStoreOpeningTime],
  );

  const findStoreInfoAndOpeningTime = useCallback(
    async (addressLabel: string) => {
      setStoreLoading(true);
      try {
        const storeData = await (getStores?.() ?? Promise.resolve([]));
        if (storeData) {
          const foundStore = storeData.find((store) => store.listAddress.startsWith(addressLabel));
          setSelectedStore(foundStore || null);
          if (foundStore) {
            await fetchStoreOpeningTime(foundStore.googlePlaceID);
          }
        }
      } catch (error) {
        console.error('Failed to fetch stores:', error);
      } finally {
        setStoreLoading(false);
      }
    },
    [getStores, fetchStoreOpeningTime],
  );

  useEffect(() => {
    if (order && isStoreCollection && order.shippingAddress) {
      const { streetName, additionalAddressInfo } = order.shippingAddress;
      const addressLabel = additionalAddressInfo ? `${streetName}, ${additionalAddressInfo}` : streetName;
      findStoreInfoAndOpeningTime(addressLabel || '');
    }
  }, [order, findStoreInfoAndOpeningTime, isStoreCollection]);

  return (
    <div className="grid grid-cols-1">
      <div className="grid gap-16 border-neutral-400 py-24 md:gap-24 lg:py-36">
        {orderInfo.map(
          ({ label, value, subValue }, index) =>
            value &&
            value.trim() && (
              <div key={index}>
                <div className="flex flex-wrap gap-5 md:gap-0">
                  <div className="md:w-156">
                    <Typography className={labelClass} asSkeleton={loading}>
                      {label + ':'}
                    </Typography>
                  </div>
                  <Typography className={valueClass} asSkeleton={loading}>
                    {label === 'Payment by' ? value.charAt(0).toUpperCase() + value.slice(1).toLowerCase() : value}
                  </Typography>
                </div>
                {subValue && (
                  <Typography className={subValueClass} asSkeleton={loading}>
                    {subValue}
                  </Typography>
                )}
              </div>
            ),
        )}
        {isStoreCollection && (
          <>
            <div key="selected-store">
              <div className="flex flex-wrap gap-5 md:gap-0">
                <div className="md:w-156">
                  <Typography className={labelClass} asSkeleton={storeLoading}>
                    {formatMessage({ id: 'your.chosen.store', defaultMessage: 'Store chosen' }) + ':'}
                  </Typography>
                </div>
                <Typography className={valueClass} asSkeleton={storeLoading}>
                  {selectedStore?.name ?? ''}
                </Typography>
              </div>
              <Typography className={subValueClass} asSkeleton={storeLoading}>
                {selectedStoreAddress1}
              </Typography>
              <Typography className={subValueClass} asSkeleton={storeLoading}>
                {selectedStoreAddress2}
              </Typography>
              <Typography className={subValueClass} asSkeleton={storeLoading}>
                {selectedStoreEmail}
              </Typography>
              <Typography className={subValueClass} asSkeleton={storeLoading}>
                {selectedStorePhone}
              </Typography>
            </div>
            <div key="opening-time">
              <div className="flex flex-wrap gap-5 md:gap-0">
                <div className="md:w-156">
                  <Typography className={labelClass} asSkeleton={openingTimeLoading}>
                    {formatMessage({ id: 'opening.hours', defaultMessage: 'Opening hours' }) + ':'}
                  </Typography>
                </div>
                <div className="mt-4 grid grid-cols-2 gap-8">
                  {storeOpeningTime?.storeHours
                    ? Object.entries(storeOpeningTime.storeHours).map(([dayKey, hours]) => {
                        const dayName = storeOpeningTime.hourResources
                          ? storeOpeningTime.hourResources[dayKey]
                          : dayKey;
                        return (
                          <React.Fragment key={dayKey}>
                            <div className="relative col-span-1 text-14 font-normal text-primary-black md:text-16">
                              {dayName}
                            </div>
                            <div className="relative col-span-1 text-14 font-normal text-primary-black md:text-16">
                              {hours}
                            </div>
                          </React.Fragment>
                        );
                      })
                    : Array.from({ length: 7 }).map((_, index) => (
                        <React.Fragment key={index}>
                          <Skeleton className="col-span-1" />
                          <Skeleton className="col-span-1" />
                        </React.Fragment>
                      ))}
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ThankYouOrderInfo;
